import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { Auth } from '@freelancer/auth';
import {
  Billing,
  CCFormExtraPayload,
  CCFormFields,
  Fields,
} from '@freelancer/billing';
import { Datastore } from '@freelancer/datastore';
import {
  Country,
  Currency,
  IssuingInstitutionCollection,
  UsersSelf,
} from '@freelancer/datastore/collections';
import { Deposits, ThreeDSContext } from '@freelancer/deposits';
import {
  FreelancerHttpConfig,
  FREELANCER_HTTP_CONFIG,
} from '@freelancer/freelancer-http';
import {
  PaymentsErrorType,
  PaymentsMessagingService,
} from '@freelancer/payments-messaging';
import { PaymentsTrackingService } from '@freelancer/payments-tracking';
import {
  methodSelectionRadioControl,
  PaymentsUtils,
} from '@freelancer/payments-utils';
import { HeadingType } from '@freelancer/ui/heading';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { InputType } from '@freelancer/ui/input';
import { Margin } from '@freelancer/ui/margin';
import { SelectItem } from '@freelancer/ui/select';
import { SpinnerSize } from '@freelancer/ui/spinner';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { TooltipPosition } from '@freelancer/ui/tooltip';
import { pattern, required } from '@freelancer/ui/validators';
import { isDefined, isFormControl, toNumber } from '@freelancer/utils';
import {
  DepositMethodApi,
  DepositRequestConfirmationApi,
  ThreedsCapabilityApi,
} from 'api-typings/payments/payments';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  take,
  tap,
} from 'rxjs/operators';
import { LatinAmericaFormFields } from '../latin-america-form/latin-america-form.component';
import { SourceDisplayMode } from '../source-template/source-template.component';
import { DepositData, GenericPayload } from '../types';
import {
  CCMethodForms,
  IFrameFieldStatus,
  latinFormFieldsToCCFormFieldsMapping,
} from './types';

@Component({
  selector: 'app-credit-card-method',
  template: `
    <app-source-template
      [group]="group"
      [sourceRadioValue]="id"
      [sourceDisplayMode]="sourceDisplayMode"
    >
      <app-source-template-header>
        <app-source-template-header-title i18n="Credit card method - title">
          Debit or Credit Card
        </app-source-template-header-title>
        <fl-text
          i18n="Credit card method - info"
          [size]="TextSize.XXXSMALL"
          [sizeDesktop]="TextSize.XXSMALL"
        >
          All major cards accepted
        </fl-text>
      </app-source-template-header>
      <app-source-template-body class="CreditCardBody">
        <fl-spinner
          *ngIf="loading && !captchaRequired"
          flTrackingLabel="DepositCreditCardInitialisationSpinner"
          [size]="SpinnerSize.LARGE"
          [overlay]="true"
        ></fl-spinner>

        <app-payments-captcha
          [captchaRequired]="(selected$ | flAsync) && captchaRequired"
          (captchaResolved)="initBilling()"
        ></app-payments-captcha>

        <fl-grid class="CreditCardInputGroup" *ngIf="!captchaRequired">
          <fl-col
            [col]="12"
            [colTablet]="8"
            [colOrder]="1"
            [colTabletOrder]="1"
            [flMarginBottom]="Margin.XSMALL"
          >
            <fl-text i18n="Credit card method - card number input label">
              Card number:
            </fl-text>
            <fl-bit
              [flMarginBottom]="
                !(panStatus$ | flAsync)?.valid &&
                (panStatus$ | flAsync)?.touched
                  ? Margin.XXXSMALL
                  : null
              "
            >
              <iframe
                class="CreditCardMethodIframe"
                id="iframe-payment-method-pan"
                sandbox="allow-forms allow-scripts allow-same-origin"
                #panIframe
                [ngClass]="{
                  Error:
                    !(panStatus$ | flAsync)?.valid &&
                    (panStatus$ | flAsync)?.touched,
                  Focus: (panStatus$ | flAsync)?.focus === true
                }"
              ></iframe>
            </fl-bit>
            <fl-text
              *ngIf="
                !(panStatus$ | flAsync)?.valid &&
                (panStatus$ | flAsync)?.touched
              "
              i18n="Credit card method - card number validation error"
              [color]="FontColor.ERROR"
              [size]="TextSize.XXSMALL"
            >
              Please provide a valid card number
            </fl-text>
          </fl-col>
          <fl-col
            [col]="(showZipCodeInput$ | flAsync) ? 4 : 6"
            [colTablet]="4"
            [colOrder]="3"
            [colTabletOrder]="2"
            [flMarginBottom]="Margin.XSMALL"
          >
            <fl-text i18n="Credit card method - expiry date input label">
              Expiry date:
            </fl-text>
            <fl-bit
              [flMarginBottom]="
                !(expiryStatus$ | flAsync)?.valid &&
                (expiryStatus$ | flAsync)?.touched
                  ? Margin.XXXSMALL
                  : null
              "
            >
              <iframe
                class="CreditCardMethodIframe"
                id="iframe-payment-method-expiry"
                sandbox="allow-forms allow-scripts allow-same-origin"
                #expiryDateIframe
                [ngClass]="{
                  Error:
                    !(expiryStatus$ | flAsync)?.valid &&
                    (expiryStatus$ | flAsync)?.touched,
                  Focus: (expiryStatus$ | flAsync)?.focus === true
                }"
              ></iframe>
            </fl-bit>
            <fl-text
              *ngIf="
                !(expiryStatus$ | flAsync)?.valid &&
                (expiryStatus$ | flAsync)?.touched
              "
              i18n="Credit card method - expiry date validation error"
              [color]="FontColor.ERROR"
              [size]="TextSize.XXSMALL"
            >
              Please provide a valid expiry date
            </fl-text>
          </fl-col>
          <fl-col
            [col]="12"
            [colTablet]="(showZipCodeInput$ | flAsync) ? 6 : 8"
            [colOrder]="2"
            [colTabletOrder]="3"
            [flMarginBottom]="Margin.XSMALL"
          >
            <fl-text i18n="Credit card method - cardholder name input label">
              Cardholder name:
            </fl-text>
            <fl-bit
              [flMarginBottom]="
                !(nameStatus$ | flAsync)?.valid &&
                (nameStatus$ | flAsync)?.touched
                  ? Margin.XXXSMALL
                  : null
              "
            >
              <iframe
                class="CreditCardMethodIframe"
                id="iframe-payment-method-name"
                sandbox="allow-forms allow-scripts allow-same-origin"
                #nameIframe
                [ngClass]="{
                  Error:
                    !(nameStatus$ | flAsync)?.valid &&
                    (nameStatus$ | flAsync)?.touched,
                  Focus: (nameStatus$ | flAsync)?.focus === true
                }"
              ></iframe>
            </fl-bit>
            <fl-text
              *ngIf="
                !(nameStatus$ | flAsync)?.valid &&
                (nameStatus$ | flAsync)?.touched
              "
              i18n="Credit card method - cardholder name validation error"
              [color]="FontColor.ERROR"
              [size]="TextSize.XXSMALL"
            >
              Please provide a card holder name
            </fl-text>
          </fl-col>

          <fl-col
            [col]="(showZipCodeInput$ | flAsync) ? 4 : 6"
            [colTablet]="(showZipCodeInput$ | flAsync) ? 3 : 4"
            [colOrder]="4"
            [flMarginBottom]="Margin.XSMALL"
          >
            <fl-bit class="InputHeading">
              <fl-text i18n="Credit card method - ccv input label">
                CCV/CVV:
              </fl-text>
              <fl-tooltip
                [message]="ccvToolTipCopy$ | flAsync"
                [position]="TooltipPosition.START_CENTER"
              >
                <fl-icon
                  [name]="'ui-help'"
                  [color]="IconColor.MID"
                  [size]="IconSize.SMALL"
                ></fl-icon>
              </fl-tooltip>
            </fl-bit>
            <fl-bit
              [flMarginBottom]="
                !(cvvStatus$ | flAsync)?.valid &&
                (cvvStatus$ | flAsync)?.touched
                  ? Margin.XXXSMALL
                  : null
              "
            >
              <iframe
                class="CreditCardMethodIframe"
                id="iframe-payment-method-cvv"
                sandbox="allow-forms allow-scripts allow-same-origin"
                #cvvIframe
                [ngClass]="{
                  Error:
                    !(cvvStatus$ | flAsync)?.valid &&
                    (cvvStatus$ | flAsync)?.touched,
                  Focus: (cvvStatus$ | flAsync)?.focus === true
                }"
              ></iframe>
            </fl-bit>
            <fl-text
              *ngIf="
                !(cvvStatus$ | flAsync)?.valid &&
                (cvvStatus$ | flAsync)?.touched
              "
              i18n="Credit card method - ccv validation error"
              [color]="FontColor.ERROR"
              [size]="TextSize.XXSMALL"
            >
              Please provide a valid CCV/CVV
            </fl-text>
          </fl-col>
          <fl-col
            *ngIf="showZipCodeInput$ | flAsync"
            [col]="4"
            [colTablet]="3"
            [colOrder]="5"
            [flMarginBottom]="Margin.XSMALL"
          >
            <fl-label
              i18n="Credit card method - zip code input label"
              [for]="'CCFormZipCodeInput'"
            >
              Zip Code:
            </fl-label>
            <ng-container
              *ngIf="ccFormGroup.controls[CCFormFields.POST_CODE] as control"
            >
              <fl-input
                *ngIf="isFormControl(control)"
                flTrackingLabel="CCFormZipCodeInput"
                [control]="control"
                [flMarginBottom]="Margin.XXSMALL"
                [id]="'CCFormZipCodeInput'"
                [type]="InputType.TEXT"
              ></fl-input>
            </ng-container>
          </fl-col>
        </fl-grid>

        <app-latin-america-form
          *ngIf="showLatinAmericaForm$ | flAsync"
          [countriesList]="countriesList$ | flAsync"
          [id]="id"
          [userInfo]="userInfo$ | flAsync"
          [flMarginBottom]="Margin.XSMALL"
          (genericFormGroup)="registerChildForm($event)"
        ></app-latin-america-form>
        <app-credit-card-logos [userInfo$]="userInfo$"></app-credit-card-logos>
      </app-source-template-body>
    </app-source-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./credit-card-method.component.scss'],
})
export class CreditCardMethodComponent
  implements OnInit, AfterViewInit, OnDestroy {
  Margin = Margin;
  HeadingType = HeadingType;
  TextSize = TextSize;
  FontColor = FontColor;
  IconColor = IconColor;
  IconSize = IconSize;
  InputType = InputType;
  isFormControl = isFormControl;
  SpinnerSize = SpinnerSize;
  TooltipPosition = TooltipPosition;
  CCFormFields = CCFormFields;
  LatinAmericaFormFields = LatinAmericaFormFields;
  SourceDisplayMode = SourceDisplayMode;

  control = new FormControl();
  countriesList$: Rx.Observable<readonly SelectItem[]>;
  loading = true;
  captchaRequired = false;
  methodFormGroup: FormGroup;
  ccFormGroup: FormGroup;
  latinFormGroup: FormGroup;
  placeholder = 'Placeholder text';
  showLatinAmericaForm$: Rx.Observable<boolean>;
  showZipCodeInput$: Rx.Observable<boolean>;
  private subscriptions: readonly Rx.Subscription[] = [];

  nameStatus$: Rx.Observable<IFrameFieldStatus>;
  panStatus$: Rx.Observable<IFrameFieldStatus>;
  cvvStatus$: Rx.Observable<IFrameFieldStatus>;
  expiryStatus$: Rx.Observable<IFrameFieldStatus>;

  threeDSPopUp: Window;
  userId: number;
  ccIframeFields: Fields;
  ccvToolTipCopy$: Rx.Observable<string>;
  selected$: Rx.Observable<boolean>;

  threeDSCtx?: ThreeDSContext;
  flnBillingDepositData?: string;

  iFrameV2Subscription?: Rx.Subscription;

  @Input() countries$: Rx.Observable<readonly Country[]>;
  @Input() depositFormGroup: FormGroup;
  @Input() currencies: readonly Currency[];
  @Input() genericPayload: GenericPayload;
  @Input() group: FormGroup;
  @Input() id: string;
  @Input() popUpEnabled: boolean;
  @Input() userInfo$: Rx.Observable<UsersSelf>;
  @Input() cartId?: number;
  @Input() sourceDisplayMode: SourceDisplayMode;
  @Input() set depositData(depositData: DepositData) {
    if (depositData) {
      this.handleDepositDataChanges(depositData);
    }
  }

  @Output() paymentCurrencySelected = new EventEmitter<Currency>();

  @ViewChild('panIframe') pan: ElementRef<HTMLIFrameElement>;
  @ViewChild('nameIframe') name: ElementRef<HTMLIFrameElement>;
  @ViewChild('expiryDateIframe') expiry: ElementRef<HTMLIFrameElement>;
  @ViewChild('cvvIframe') cvv: ElementRef<HTMLIFrameElement>;

  constructor(
    private auth: Auth,
    private billing: Billing,
    private changeDetectorRef: ChangeDetectorRef,
    private datastore: Datastore,
    private fb: FormBuilder,
    private http: HttpClient,
    private paymentsMessaging: PaymentsMessagingService,
    private paymentsTracking: PaymentsTrackingService,
    private deposits: Deposits,
    @Inject(FREELANCER_HTTP_CONFIG)
    private freelancerHttpConfig: FreelancerHttpConfig,
    private paymentsUtils: PaymentsUtils,
  ) {}

  ngOnInit() {
    this.methodFormGroup = this.fb.group({});
    this.ccFormGroup = this.fb.group({
      [CCFormFields.CCV]: [undefined, required($localize`CCV required`)],
      [CCFormFields.NAME]: [undefined, required($localize`Name required`)],
      [CCFormFields.NUMBER]: [
        undefined,
        required($localize`CC Number required`),
      ],
      [CCFormFields.EXPIRY]: [
        undefined,
        required($localize`Expiry date required`),
      ],
    });
    this.methodFormGroup.addControl(CCMethodForms.CC_FORM, this.ccFormGroup);
    this.paymentsUtils.registerFormGroup(
      this.methodFormGroup,
      this.depositFormGroup,
      this.id,
    );

    this.countriesList$ = this.countries$.pipe(
      map(countries =>
        countries
          .filter(country => ['BR', 'MX', 'CL'].includes(country.code))
          .map(country => ({
            displayText: country.name,
            value: country.code,
          })),
      ),
    );

    this.showZipCodeInput$ = this.userInfo$.pipe(
      map(
        userInfo =>
          userInfo.address && userInfo.address.country === 'United States',
      ),
      filter(isDefined),
      tap(val => {
        if (val) {
          const zipCodeFormControl = new FormControl('', [
            required($localize`Please provide a valid Zip Code.`),
            pattern(/^.{1,12}$/, $localize`Please provide a valid Zip Code.`),
          ]);
          this.ccFormGroup.setControl(
            CCFormFields.POST_CODE,
            zipCodeFormControl,
          );
        }
      }),
    );

    this.showLatinAmericaForm$ = this.userInfo$.pipe(
      map(userInfo => {
        if (userInfo.address) {
          return ['Brazil', 'Mexico', 'Chile'].includes(
            userInfo.address.country || '',
          );
        }
        return false;
      }),
    );

    this.selected$ = this.group.valueChanges.pipe(
      startWith(this.group.value),
      map(value => value[methodSelectionRadioControl] === this.id),
    );

    this.threeDSCtx = undefined;
  }

  ngAfterViewInit() {
    const userId$ = this.auth.getUserId();
    const userIdPromise = userId$
      .pipe(map(toNumber), filter(isDefined), take(1))
      .toPromise();

    this.ccIframeFields = {
      pan: this.pan.nativeElement,
      cvv: this.cvv.nativeElement,
      name: this.name.nativeElement,
      expiry: this.expiry.nativeElement,
    };

    userIdPromise.then(userId => {
      this.userId = userId;
      this.initBilling();
    });

    if (this.popUpEnabled) {
      this.subscriptions = [
        ...this.subscriptions,
        this.deposits
          .getThreeDSTriggerStream()
          .subscribe(data => this.deposits.popThreeDSModal(data)),
      ];
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
    if (this.iFrameV2Subscription) {
      this.iFrameV2Subscription.unsubscribe();
    }
  }

  registerChildForm(event: FormGroup) {
    this.ccFormGroup.setControl(CCMethodForms.LATIN_AMERICA_FORM, event);
    this.latinFormGroup = event;
    this.paymentsUtils.registerFormGroup(
      this.methodFormGroup,
      this.depositFormGroup,
      this.id,
    );
  }

  handleDepositDataChanges(depositData: DepositData) {
    const extraPayLoad: CCFormExtraPayload = {};
    extraPayLoad[CCFormFields.ISSUING_CURRENCY_CODE] =
      depositData.paymentCurrency.code;

    if (this.ccFormGroup.controls[CCFormFields.POST_CODE]) {
      extraPayLoad[CCFormFields.POST_CODE] = this.ccFormGroup.controls[
        CCFormFields.POST_CODE
      ].value;
    }

    if (this.latinFormGroup) {
      extraPayLoad[CCFormFields.PAYMENT_CURRENCY_CODE] =
        depositData.paymentCurrency.code;
      const latinFormGroupControls = this.latinFormGroup.controls;
      Object.keys(latinFormGroupControls).forEach(index => {
        const key = latinFormFieldsToCCFormFieldsMapping.get(
          index as LatinAmericaFormFields,
        );
        if (key) {
          extraPayLoad[key] = latinFormGroupControls[index].value;
        }
      });
    }

    const updatedExtraPayload = this.deposits.populateBrowserInfo(extraPayLoad);

    this.billing.submit(
      depositData.depositAmount,
      depositData.depositCurrency.code,
      updatedExtraPayload,
    );

    this.paymentsUtils
      .listenForDepositResult()
      .then(data => {
        this.initBilling();
        const message = this.paymentsUtils.handleInterframeDepositResult(data);
        if (message) {
          this.paymentsMessaging.pushMessage(message);
        }
      })
      .catch(error => {
        this.initBilling();
        this.deposits.emitErrorMessage(error.message, PaymentsErrorType.ERROR);
      });
  }

  initBilling() {
    const action =
      this.genericPayload.action === 'verifyHosted'
        ? 'verifyHosted'
        : this.popUpEnabled
        ? 'getIframeV2'
        : 'getHosted';
    const payload = {
      ...this.genericPayload,
      // Specific
      deposit_method: DepositMethodApi.FLN_BILLING,
      action,
      cancel_url: `${this.freelancerHttpConfig.baseUrl}/deposit/`,
      // Hardcoded - inconsequential values. Regardless of what we put here
      // the user could change the amount or currency after page load.
      amount: 30,
      currency_id: 1,
      credit_card_gateway: 'gc',
      domain: '',
    };

    const urlPromise = this.deposits
      .deposit(payload)
      .toPromise()
      .then(response => {
        if (response.status === 'success') {
          return response.result.url || '';
        }
        this.paymentsMessaging.pushError({
          errorType: PaymentsErrorType.ERROR,
          errorCode: response.errorCode,
          eventId: response.requestId,
        });
        return '';
      })
      .catch(error => {
        this.paymentsMessaging.pushError({
          errorType: PaymentsErrorType.ERROR,
          errorMessage: error.message,
        });
        return '';
      });

    this.billing.initialise(() => urlPromise, this.ccIframeFields);

    const messageStream$ = this.billing.getMessageStream();

    this.subscriptions = [
      ...this.subscriptions,
      messageStream$.subscribe(message => {
        const trackingData = {
          action: 'change',
          label: 'CreditCardDetailsInputs',
          value: JSON.stringify(message),
        };
        this.paymentsTracking.pushTrackingData(trackingData);
      }),
    ];

    this.nameStatus$ = this.getIFrameFieldStatusStream(
      ['name'],
      'name',
      CCFormFields.NAME,
    );
    this.panStatus$ = this.getIFrameFieldStatusStream(
      ['pan'],
      'pan',
      CCFormFields.NUMBER,
    );
    this.cvvStatus$ = this.getIFrameFieldStatusStream(
      ['cvv'],
      'cvv',
      CCFormFields.CCV,
    );
    this.expiryStatus$ = this.getIFrameFieldStatusStream(
      ['month', 'year'],
      'expiry',
      CCFormFields.EXPIRY,
    );

    const cardMessage$ = messageStream$.pipe(
      filter(m => 'pan' in m || 'cvv' in m),
    );

    const bin$ = cardMessage$.pipe(
      filter(m => m.pan.length >= 6),
      map(m => toNumber(m.iin)),
      distinctUntilChanged(),
    );

    const issuingInstitutionCollection = this.datastore.collection<IssuingInstitutionCollection>(
      'issuingInstitution',
      query => query.where('bin', '==', bin$),
    );

    this.subscriptions = [
      ...this.subscriptions,
      issuingInstitutionCollection.status$.subscribe(status => {
        if (status.error) {
          this.paymentsMessaging.pushError({
            errorType: PaymentsErrorType.ERROR_DATASTORE,
            errorCode: status.error.errorCode,
            retry: status.error.retry,
          });
        }
      }),
    ];

    const iin$ = issuingInstitutionCollection.valueChanges().pipe(
      map(i => i[0]), // this should only return one result
      filter(isDefined),
    );

    this.ccvToolTipCopy$ = iin$.pipe(
      map(issuingInstitution =>
        ['AMERICAN EXPRESS', 'AMEX'].indexOf(issuingInstitution.brand) >= 0
          ? 'The 4 digits displayed on the front of your card'
          : 'The last 3 digits displayed on the back of your card',
      ),
      distinctUntilChanged(),
      startWith('The last 3 digits displayed on the back of your card'),
    );

    this.subscriptions = [
      ...this.subscriptions,
      iin$.subscribe(issuingInstitution => {
        if (issuingInstitution.currency) {
          this.paymentCurrencySelected.emit(issuingInstitution.currency);
        }
      }),
    ];

    const listOfFields = Object.values(this.ccIframeFields);
    let loadedFields = 0;
    listOfFields.forEach(
      field =>
        (field.onload = () => {
          if (++loadedFields === listOfFields.length) {
            this.loading = false;
            this.changeDetectorRef.detectChanges();
          }
        }),
    );

    /**
     * This will be emitting only for getIframeV2
     * which limits it to deposit flow
     */
    const submissionEvent$ = this.getIframeSubmissionStream();

    if (!this.iFrameV2Subscription) {
      this.iFrameV2Subscription = submissionEvent$.subscribe(data => {
        this.flnBillingDepositData = data;
        const confirmation: DepositRequestConfirmationApi = {
          user_id: this.genericPayload.user_id,
          deposit_method: DepositMethodApi.FLN_BILLING,
          fln_billing_data: data,
          credit_card_gateway: 'gc',
          cart_id: this.cartId,
          threeds_capability: ThreedsCapabilityApi.VER2,
          tracking_token: this.genericPayload.tracking_token,
          action:
            this.genericPayload.action === 'verifyHosted'
              ? 'verifyPayment'
              : '',
        };
        this.deposits.creditCardDeposit(confirmation);
      });
    }

    // It is critical that we initialise the iframes (set their src)
    // after the billing service has been initialised.
    urlPromise.then((url: string) => {
      Object.values(this.ccIframeFields).forEach(f => (f.src = url));
      this.checkIfCaptchaRequired(url).then((result: boolean) => {
        this.captchaRequired = result;
      });
    });
    // Since this function is called in an asynchronous context
    // we must alert Angular's change detection to the changes we have made.
    // Specifically, the validation observables (which are null on init)
    this.changeDetectorRef.detectChanges();
  }

  checkIfCaptchaRequired(url: string): Promise<any> {
    return this.http
      .get(url, { responseType: 'text' })
      .pipe(take(1))
      .toPromise()
      .then(_ => false)
      .catch(
        e =>
          // Means it is a CORS error, which shall be caused by the captcha
          e.status === 0,
      );
  }

  validateControl(control: AbstractControl, valid: boolean | undefined) {
    if (valid) {
      control.setErrors(null);
      control.setValue(true); // we need it to pass 'required'
    } else {
      control.setErrors({
        INCORRECT: true,
      });
    }
  }

  getIframeSubmissionStream(): Rx.Observable<string> {
    const messageStream$ = this.billing.getMessageStream();
    return messageStream$.pipe(
      filter(m => 'submission' in m),
      map(m => m.submission),
    );
  }

  getIFrameFieldStatusStream(
    validFilterStrings: readonly string[],
    frameName: string,
    ccFormFieldName: CCFormFields,
  ): Rx.Observable<IFrameFieldStatus> {
    const messageStream$ = this.billing.getMessageStream();
    const formControl = this.ccFormGroup.controls[ccFormFieldName];
    const validationEventMessage$ = messageStream$.pipe(
      filter(m => validFilterStrings.some(validString => validString in m)),
      map(m =>
        'valid' in m
          ? m.valid
          : m[frameName] && 'valid' in m[frameName]
          ? m[frameName].valid
          : undefined,
      ),
      startWith(undefined),
    );
    this.subscriptions = [
      ...this.subscriptions,
      validationEventMessage$.subscribe(valid =>
        this.validateControl(formControl, valid),
      ),
    ];
    const focusEventMessage$ = messageStream$.pipe(
      // This means the user has interacted with the field, so mark it dirty
      filter(
        m =>
          'focusEvent' in m &&
          'iframe' in m.focusEvent &&
          m.focusEvent.iframe === frameName,
      ),
      tap(() => this.ccFormGroup.controls[ccFormFieldName].markAsDirty()),
      map(m => m.focusEvent.focus),
      startWith(false),
    );

    return Rx.combineLatest([
      formControl.statusChanges.pipe(startWith('VALID')),
      focusEventMessage$,
    ]).pipe(
      map(([status, focus]) => ({
        valid: status === 'VALID',
        touched: formControl.touched || formControl.dirty,
        focus,
      })),
      tap(m => {
        if (m.touched) {
          this.billing.style({ [frameName]: m.valid });
        }
      }),
    );
  }
}
